/**
 * Generic DataLayer (genDL)
 * Pushes individual product events (e.g. Amplitude) to the genDL object
 * Stub `window.genDL = window.genDL || [];` is placed in the <head>, this file implements the actual events
 * @version 1.0.0
 * @author Jaroslav Slaby <jaroslav.slaby@avast.com>
 * @copyright Avast Software s.r.o &copy;2026
 */

(function genDLEvents() {
	'use strict';

	window.genDL = window.genDL || [];

	try {
		var genDLObj = {
			// Push a single event to genDL
			// name: string event name (e.g. 'consent_updated')
			// properties: plain object with event-specific properties (merged next to "event", not nested)
			pushEvent: function (name, properties) {
				var payload = properties || {};
				payload.event = name;

				genDL.push(payload);
				genDLObj.keepInDataLayer(payload);
			},

			// Tealium's utag.js treats genDL as a QUEUE, not a log: once its data layer listener
			// initializes it forwards whatever is queued and then empties the array outright with
			// splice(0, n). Confirmed live on the AVG preview by trapping the array's mutators -- the
			// call comes from utag.js r.process -> r.listener -> r.flatten. That's why events pushed
			// during page load were delivered but then vanished from window.genDL, while the ones fired
			// later (clicks, modal impressions) stayed.
			//
			// So push straight away -- delivery timing stays exactly as the consumer expects -- and just
			// watch each payload afterwards, putting it back if the drain took it. Note the drain can
			// come late: CHEQ delays loading utag until the consent banner is dealt with, so on a first
			// visit it can be many seconds in, hence the generous window below.
			//
			// The payload goes back in with the NATIVE push on purpose: the consumer already received it
			// from the push above, and going through the overridden push a second time would forward it
			// again and duplicate it downstream. Each payload is restored at most once, so a consumer
			// that keeps draining can't turn this into a loop.
			trackedPayloads: [],
			keepInDataLayer: function (payload) {
				genDLObj.trackedPayloads.push({ payload: payload, expiresAt: Date.now() + 30000 });

				if (genDLObj.dataLayerWatcher) {
					return;
				}

				genDLObj.dataLayerWatcher = setInterval(function () {
					var stillTracked = [];

					for (var i = 0; i < genDLObj.trackedPayloads.length; i++) {
						var tracked = genDLObj.trackedPayloads[i];

						if (window.genDL.indexOf(tracked.payload) === -1) {
							Array.prototype.push.call(window.genDL, tracked.payload);
							continue;
						}

						if (Date.now() < tracked.expiresAt) {
							stillTracked.push(tracked);
						}
					}

					genDLObj.trackedPayloads = stillTracked;

					if (!stillTracked.length) {
						clearInterval(genDLObj.dataLayerWatcher);
						genDLObj.dataLayerWatcher = null;
					}
				}, 250);
			},

			// Check if element has specific data attribute and get its value
			checkAttribute: function (element, attributeName) {
				return element && element.hasAttribute(attributeName) ? element.getAttribute(attributeName) : '';
			},

			// Get the value of a specific cookie by name, or undefined if it doesn't exist
			getCookie: function (name) {
				var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\\/+^])/g, '\\$1') + '=([^;]*)'));
				return match ? decodeURIComponent(match[1]) : undefined;
			},

			// Get the value of a specific URL query parameter, or undefined if it doesn't exist / is empty
			getUrlParam: function (name) {
				var value = new URLSearchParams(window.location.search).get(name);
				return value ? value : undefined;
			},

			// Are we on a download-thank-you page? Matches both the public URL (download-thank-you.php)
			// and the CMS "dtp_*" page variants, which the template itself normalizes to the same
			// page_name (see `strpos($config['fileName'], 'dtp_') === 0` in the analytics snippet).
			isThankYouPage: function () {
				return /(^|\/)(download-thank-you|dtp[_-])/i.test(window.location.pathname);
			},

			// Remove unwanted URL parameters from a link (same denylist as singleDL-secondary.js)
			removeUnwantedParamsFromLink: function (href) {
				var hash = window.location.hash,
					url = new URL(href, window.location.href),
					urlParams = new URLSearchParams(url.search),
					unwantedParams = ['ASWPARAM', 'aswparam', '_ga', '_gl'];

				for (var i = 0; i < unwantedParams.length; i++) {
					urlParams.delete(unwantedParams[i]);
				}

				url.search = '';
				var hrefWithoutParams = url.toString().split('#')[0];
				var query = urlParams.toString();
				return hrefWithoutParams + (query ? '?' + query : '') + hash;
			},

			//
			// page_viewed
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/945662786/BASE+CODE
			// MOVED 2026-08-24 -- this event now fires from an inline <head> script in the CMS page
			// template instead of from here. Spec requires it to fire right after the 3 required <head>
			// script blocks (Amplitude Experiment, genDL array stub, Tealium loader); this file only
			// loads in the footer, which is too late for that ordering requirement. See the CMS snippet
			// (chat history 2026-08-24) for the inline equivalent of the logic that used to live here --
			// same window.genDLObj (server-provided) properties, plus gen_id now read directly from the
			// `gd_id` cookie (confirmed present on live AVG pages) instead of needing a server value.
			//
			// consent_updated
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035628/consent_updated
			// Fires when a user changes their consent settings via the consent banner or privacy settings page
			//
			// consent_banner_impression
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035623/consent_banner_impression
			// IMPLEMENTED SEPARATELY 2026-08-25 -- same reasoning as page_viewed above: fires "when the
			// banner is rendered", which for a first-time visitor can happen before this footer-loaded
			// file has even run. Lives instead in an inline <head> script (added via CMS, right after
			// the genDL array stub -- confirmed live that CHEQ's Bootstrap.js script tag sits AFTER that
			// stub in <head>, so a MutationObserver set up there is guaranteed to already be watching
			// before CHEQ can insert/show #ensNotifyBanner). Duplicates the same privacy_settings cookie
			// logic as consentUpdated below (self-contained, can't depend on genDLObj not loaded yet).
			//
			consentUpdated: function (event) {
				// Verified live 2026-08-25 (incognito, fresh visitor, page load only -- no click at
				// all): CHEQ fires cheqCookieConsentHasChanged a FIRST time on its own, applying its
				// default/implied-consent state before the user has done anything (genDL showed
				// consent_updated with no preceding interaction). The spec only wants this event on an
				// actual user-driven change, so gate on SYMANTEC__hasUserChangedConsent -- confirmed live
				// (2026-08-25, "Reject All" click) that CHEQ itself only flips this cookie to "1" once
				// the user has genuinely interacted ("0" beforehand, "1" right after).
				if (genDLObj.getCookie('SYMANTEC__hasUserChangedConsent') !== '1') {
					return;
				}

				// CHEQ's cheqCookieConsentHasChanged event also carries the already-parsed state
				// directly as event.detail.consent (e.g.
				// {necessary:"1",preference:"0",performance:"0",advertising:"0"}) -- preferred over
				// re-reading cookies (avoids any write/read race). Cookie read kept as a fallback for
				// robustness (e.g. if this is ever invoked without an event).
				var categories = ['Performance', 'Preference', 'Advertising'];
				var detailConsent = event && event.detail && event.detail.consent;
				var values = [];
				for (var i = 0; i < categories.length; i++) {
					var detailValue = detailConsent && detailConsent[categories[i].toLowerCase()];
					values.push(typeof detailValue !== 'undefined' ? detailValue : genDLObj.getCookie('SYMANTEC_ENSIGHTEN_PRIVACY_' + categories[i]));
				}

				var optedIn = true;
				var optedOut = true;
				var hasUnknown = false;
				for (var j = 0; j < values.length; j++) {
					if (typeof values[j] === 'undefined') {
						hasUnknown = true;
					} else if (values[j] === '1') {
						optedOut = false;
					} else {
						optedIn = false;
					}
				}

				var privacySettings;
				if (hasUnknown) {
					privacySettings = 'pending';
				} else if (optedIn) {
					privacySettings = 'opted_in';
				} else if (optedOut) {
					privacySettings = 'opted_out';
				} else {
					privacySettings = 'partial';
				}

				genDLObj.pushEvent('consent_updated', {
					privacy_settings: privacySettings
				});
			},

			//
			// product_downloaded
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035724/product_downloaded
			// Fires when a user initiates the download of a Gen Digital product installer file
			//
			// Fired ONLY from the download-thank-you page, never from the click on a download button --
			// deliberately mirroring what Adobe Analytics has done here all along. Its only download
			// trigger is an `onLoadDownload` marker the CMS renders into the thank-you page:
			//     <span data-tracking="'onLoadDownload', {'product': 'avm-hp', 'category': ''}"></span>
			// and there is no download tracking on the buttons themselves -- checked live on
			// antivirus-for-android, free-antivirus-download, homepage and secure-vpn, where the only
			// data-tracking attributes are a couple of 'click' + icid banner markers.
			//
			// The click really is the wrong place for it, which live testing confirmed (16/16 homepage
			// download buttons): every one of them merely navigates to download-thank-you.php, and the
			// click can still be intercepted by the wrong-download "oops" overlay (see
			// notificationWrongDownload.js) so that no download ever happens. The actual download starts
			// on the thank-you page, where dtp.js does `window.location = avgDownloadLink`.
			//
			// Known cost of matching Adobe: downloads that never render a thank-you page aren't counted,
			// i.e. app-store links (most Android/iOS buttons go straight to play.google.com) and the
			// rarely-used DTYP download popup. Adobe doesn't count those either -- deliberate parity, so
			// the two systems can be reconciled rather than each being wrong in its own way.
			//
			// Verified against live markup (web-preview, 2026-08-19 & 2026-08-21):
			//   - product_name:     the `product` URL param -- PIM product code (e.g. "FREEGSR-HP"). Not a
			//                       human-readable name, but the recognizable product identifier.
			//   - product_platform: parsed out of the installer URL (see getPlatformFromDownloadLink).
			//   - deployed_product_sku / deployed_product_name: sourced from https://static.avast.com/client.js
			//     (see GLOWEB-3195 spike, confluence pageId=990719800). This file is normally empty; if the
			//     visitor has an Avast/AVG product installed on Windows, its Web Shield component locally
			//     rewrites the response to set window.avastGlobals.client. Best-effort / Windows-only --
			//     will be omitted for the vast majority of visitors who don't have this. Two known shapes:
			//       { product_list: [{ product: "Avast Premium Security", version: "22.3.10022" }, ...] }
			//       { p_pro: "130" } (internal numeric product code)
			//
			// Called from dtp.js at the point it starts the installer navigation -- see the wiring at the
			// bottom of this file. Deliberately NOT gated on window.avgDownloadLink: that link is set by
			// a separate inline <script> in the page footer, so gating on it made the whole event hostage
			// to script timing and any load where this file ran first dropped product_downloaded silently
			// and for good. It only feeds two OPTIONAL properties (download_url, product_platform), so
			// its absence degrades the event rather than cancelling it.
			productDownloadedOnThankYouPage: function () {
				// Idempotent -- both dtp.js and this file's own init can reach it, depending on which of
				// the two loads first. thankYouDownloadFired keeps it counted only once.
				if (genDLObj.thankYouDownloadFired || !genDLObj.isThankYouPage()) {
					return;
				}

				var downloadLink = typeof window.avgDownloadLink === 'string' ? window.avgDownloadLink : '';

				var properties = {};

				var productName = genDLObj.getUrlParam('product');
				if (productName) {
					properties.product_name = [productName];
				}

				if (downloadLink) {
					var productPlatform = genDLObj.getPlatformFromDownloadLink(downloadLink);
					if (productPlatform.length) {
						properties.product_platform = productPlatform;
					}

					properties.download_url = genDLObj.removeUnwantedParamsFromLink(downloadLink);
				}

				var deployedProduct = genDLObj.getDeployedProduct();
				if (deployedProduct.sku) {
					properties.deployed_product_sku = deployedProduct.sku;
				}
				if (deployedProduct.name) {
					properties.deployed_product_name = deployedProduct.name;
				}

				genDLObj.pushEvent('product_downloaded', properties);
				// Only after the push actually succeeded -- setting it upfront would let a throw while
				// building the properties above permanently swallow the dtp.js retry as a no-op
				genDLObj.thankYouDownloadFired = true;
			},

			// Parses the platform segment out of a bits.avcdn.net installer URL (e.g.
			// ".../platform_WIN_AVG/..." or ".../platform_MAC_AVG/...") -- confirmed live for Windows
			// and Mac. Returns [] (property omitted) if the pattern isn't recognized.
			getPlatformFromDownloadLink: function (downloadLink) {
				var platformMap = { WIN: 'windows', MAC: 'mac', ANDROID: 'android', IOS: 'ios' };
				var match = downloadLink.match(/platform_([A-Z]+)_/);
				return match && platformMap[match[1]] ? [platformMap[match[1]]] : [];
			},

			// Reads whatever client.js (Windows-only Web Shield injection, see the deployed_product note
			// above) has set on window.avastGlobals.client, if anything. Returns {} when no product was detected
			// (i.e. no Avast/AVG product installed, non-Windows, or client.js hasn't resolved yet).
			getDeployedProduct: function () {
				var client = window.avastGlobals && window.avastGlobals.client;
				if (!client) {
					return {};
				}

				if (Array.isArray(client.product_list) && client.product_list.length && client.product_list[0].product) {
					return { name: client.product_list[0].product };
				}

				if (client.p_pro) {
					return { sku: String(client.p_pro) };
				}

				return {};
			},

			// Allowlist of "media"/document file extensions tracked by file_downloaded. Product installer
			// extensions (exe, dmg, apk, ...) are intentionally NOT here -- those go through
			// product_downloaded instead (see above), and everything else (html, php, ...) is just
			// regular page navigation, not a file download.
			fileExtensions: [
				// documents
				'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'rtf', 'odt', 'ods', 'odp',
				// archives
				'zip', 'rar', '7z', 'tar', 'gz',
				// media
				'mp3', 'mp4', 'wav', 'avi', 'mov', 'wmv'
			],

			//
			// file_downloaded
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035658/file_downloaded
			// Fires when a user clicks a link to a document/media file (see fileExtensions allowlist above)
			//
			fileDownloaded: function (link) {
				var url = new URL(link.href, window.location.href);
				if (url.protocol !== 'http:' && url.protocol !== 'https:') {
					// e.g. mailto:/tel: links -- not a file, and mailto: locals can contain
					// a dot before the domain which would otherwise false-match as a file extension
					return;
				}

				var match = url.pathname.match(/\.([a-zA-Z0-9]+)$/);
				if (!match) {
					return;
				}

				var fileType = match[1].toLowerCase();
				if (genDLObj.fileExtensions.indexOf(fileType) === -1) {
					return;
				}

				genDLObj.pushEvent('file_downloaded', {
					file_name: url.pathname.substring(url.pathname.lastIndexOf('/') + 1),
					file_type: fileType,
					file_url: genDLObj.removeUnwantedParamsFromLink(link.href)
				});
			},

			//
			// video_started / video_progress / video_completed
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035771/video_started
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035769/video_progress
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/954035767/video_completed
			//
			// Verified live (avg.com/en-us/secure-identity, 2026-08-19): videos are YouTube-nocookie
			// iframes with ?enablejsapi=1, opened in a Bootstrap modal that appends &autoplay=1 to the
			// src on open. We wrap each such iframe with the official YouTube IFrame API
			// (window.YT.Player) to get real onStateChange/getCurrentTime/getDuration data -- confirmed
			// this works fine despite the iframe's `sandbox` attribute, and loading
			// https://www.youtube.com/iframe_api isn't blocked by CHEQ.
			//
			// IMPORTANT: changing an iframe's `src` (e.g. the modal adding &autoplay=1 on open) is a full
			// navigation that destroys the old document. Every matching iframe is wrapped immediately
			// (covers videos whose src never changes again) AND re-wrapped on every subsequent native
			// `load` event (covers the modal-reopen/autoplay case). Re-wrapping the *same* DOM node a
			// second time leaves the YouTube API broken (events fire but player methods like
			// getDuration() never attach) -- confirmed live. So past the first wrap, we clone+replace the
			// iframe with a fresh DOM node before wrapping it again, and wait for that clone's own
			// (self-triggered, since inserting an iframe with a src re-navigates it) `load` event before
			// calling YT.Player on it -- wrapping synchronously, before that fresh navigation completes,
			// silently fails too.
			//
			videoPlayers: {}, // keyed by iframe id -- per-video started/milestone/interval tracking state
			videoMilestones: [25, 50, 75], // video_completed handles the 100% mark separately

			// Lazily loads the official YouTube IFrame API (no-op if already loaded/loading)
			ensureYouTubeApiLoaded: function (callback) {
				if (window.YT && window.YT.Player) {
					callback();
					return;
				}

				genDLObj.youtubeApiCallbacks = genDLObj.youtubeApiCallbacks || [];
				genDLObj.youtubeApiCallbacks.push(callback);

				if (genDLObj.youtubeApiLoading) {
					return;
				}
				genDLObj.youtubeApiLoading = true;

				var previousReady = window.onYouTubeIframeAPIReady;
				window.onYouTubeIframeAPIReady = function () {
					if (typeof previousReady === 'function') {
						previousReady();
					}
					var callbacks = genDLObj.youtubeApiCallbacks || [];
					genDLObj.youtubeApiCallbacks = [];
					for (var i = 0; i < callbacks.length; i++) {
						callbacks[i]();
					}
				};

				var tag = document.createElement('script');
				tag.src = 'https://www.youtube.com/iframe_api';
				document.head.appendChild(tag);
			},

			// Finds YouTube embed iframes (youtube.com or youtube-nocookie.com, with the enablejsapi=1
			// param required for postMessage/IFrame API communication) anywhere on the page
			findYouTubeIframes: function () {
				return document.querySelectorAll(
					'iframe[src*="youtube.com/embed/"][src*="enablejsapi=1"], ' +
					'iframe[src*="youtube-nocookie.com/embed/"][src*="enablejsapi=1"]'
				);
			},

			// Scans the page for not-yet-seen YouTube iframes and wraps each one. Only reacts to
			// `load` events afterwards (see wrapVideoIframe) -- NOT re-invoked by the DOM mutation
			// observer for iframes already seen, since cloning+replacing an iframe (done there on the
			// 2nd+ wrap) is itself a mutation, which would otherwise re-trigger this and loop forever.
			// Claiming happens synchronously (a plain attribute, not the async-loaded videoPlayers
			// registry) so a burst of mutations firing before the YouTube API finishes loading can't
			// each independently queue up their own wrap of the same iframe.
			scanForVideos: function () {
				var iframes = genDLObj.findYouTubeIframes();
				var newIframes = [];
				for (var i = 0; i < iframes.length; i++) {
					if (!iframes[i].getAttribute('data-gendl-video-claimed')) {
						iframes[i].setAttribute('data-gendl-video-claimed', '1');
						newIframes.push(iframes[i]);
					}
				}
				if (!newIframes.length) {
					return;
				}

				genDLObj.ensureYouTubeApiLoaded(function () {
					for (var i = 0; i < newIframes.length; i++) {
						genDLObj.wrapVideoIframe(newIframes[i]);
					}
				});
			},

			wrapVideoIframe: function (iframe) {
				if (!iframe.id) {
					iframe.id = 'genDL-video-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
				}

				// Not the first wrap for this id -- clone+replace to get a fresh DOM node (see note
				// above), then wait for ITS OWN load (from being freshly inserted) before continuing
				if (genDLObj.videoPlayers[iframe.id]) {
					var clone = iframe.cloneNode(true);
					iframe.parentNode.replaceChild(clone, iframe);
					clone.addEventListener('load', function selfLoad() {
						clone.removeEventListener('load', selfLoad);
						genDLObj.finishWrapVideoIframe(clone);
					}, { once: true });
					return;
				}

				genDLObj.finishWrapVideoIframe(iframe);
			},

			finishWrapVideoIframe: function (iframe) {
				iframe.addEventListener('load', function () {
					genDLObj.wrapVideoIframe(iframe);
				}, { once: true });

				var state = { started: false, milestonesSeen: {}, interval: null };
				genDLObj.videoPlayers[iframe.id] = state;

				state.player = new window.YT.Player(iframe.id, {
					events: {
						onStateChange: function (e) {
							genDLObj.onVideoStateChange(state, e);
						}
					}
				});
			},

			onVideoStateChange: function (state, e) {
				var YT = window.YT;

				if (e.data === YT.PlayerState.PLAYING) {
					if (!state.started) {
						state.started = true;
						genDLObj.videoStarted(state.player);
					}
					if (!state.interval) {
						state.interval = setInterval(function () {
							genDLObj.checkVideoProgress(state);
						}, 1000);
					}
				} else if (state.interval) {
					clearInterval(state.interval);
					state.interval = null;
				}

				if (e.data === YT.PlayerState.ENDED) {
					genDLObj.videoCompleted(state.player);
					// Reset so a replay of the same embed fires video_started/video_progress again
					state.started = false;
					state.milestonesSeen = {};
				}
			},

			checkVideoProgress: function (state) {
				var player = state.player,
					duration = player.getDuration();

				if (!duration) {
					return;
				}

				var percent = Math.floor((player.getCurrentTime() / duration) * 100);
				for (var i = 0; i < genDLObj.videoMilestones.length; i++) {
					var milestone = genDLObj.videoMilestones[i];
					if (percent >= milestone && !state.milestonesSeen[milestone]) {
						state.milestonesSeen[milestone] = true;
						genDLObj.videoProgress(player, milestone);
					}
				}
			},

			// The YouTube-reported video title (e.g. "AVG Secure Identity (US)") -- a more reliable,
			// human-readable name than the iframe's own title/data attributes
			getVideoName: function (player) {
				try {
					var data = player.getVideoData();
					return data && data.title ? data.title : undefined;
				} catch (error) {
					return undefined;
				}
			},

			videoStarted: function (player) {
				var properties = { video_duration: Math.round(player.getDuration()) };
				var videoName = genDLObj.getVideoName(player);
				if (videoName) {
					properties.video_name = videoName;
				}
				genDLObj.pushEvent('video_started', properties);
			},

			videoProgress: function (player, milestone) {
				var properties = {
					video_percent: milestone,
					video_current_time: Math.round(player.getCurrentTime()),
					video_duration: Math.round(player.getDuration())
				};
				var videoName = genDLObj.getVideoName(player);
				if (videoName) {
					properties.video_name = videoName;
				}
				genDLObj.pushEvent('video_progress', properties);
			},

			videoCompleted: function (player) {
				var properties = {
					video_duration: Math.round(player.getDuration()),
					video_current_time: Math.round(player.getCurrentTime())
				};
				var videoName = genDLObj.getVideoName(player);
				if (videoName) {
					properties.video_name = videoName;
				}
				genDLObj.pushEvent('video_completed', properties);
			},

			//
			// modal_impression
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/946053435/modal_impression
			// Fires when a modal dialog is rendered and visible to the user.
			//
			// AVG has no single modal implementation -- four different patterns are covered below:
			//   1. Bootstrap modals (`.modal`, both jQuery BS3 and the vanilla BSN v4 used on v2/facelift
			//      pages) -- these fire a `shown.bs.modal` event, exactly like the existing AA tracking
			//      in singleDL-secondary.js (proven to work with a plain native addEventListener, even
			//      though BS3 dispatches it via jQuery's `.trigger()`).
			//   2. The DTYP download popup (`[data-behavior="downloadPopup"]`) -- mirrors the same
			//      heuristic already used in singleDL-secondary.js: it's implicitly shown right after a
			//      download-link click, and doesn't reliably fire its own show event.
			//   3. The "wrong download" (oops) notification overlay -- shown by adding a `show-popup`
			//      class (see avg/j/global/notificationWrongDownload.js).
			//   4. Generic `.overlay-box` popups -- shown by adding an `is-visible` class, identified via
			//      their own `data-box` attribute (see avg/j/global/overlayPopup.js).
			// CHEQ's own cookie-consent modal is intentionally NOT tracked here -- it's third-party UI,
			// not part of the product experience.
			//
			modalImpression: function (modalName, modalCategory, modalType) {
				var properties = {};
				if (modalName) {
					properties.modal_name = modalName;
				}
				if (modalCategory) {
					properties.modal_category = modalCategory;
				}
				if (modalType) {
					properties.modal_type = modalType;
				}
				genDLObj.pushEvent('modal_impression', properties);
			},

			// Best available identifier for a modal/overlay element: AEM component name first (most
			// semantic), falling back to its DOM id. Returns undefined if neither is present.
			getModalName: function (element) {
				return genDLObj.checkAttribute(element, 'data-cmp-name') || element.id || undefined;
			},

			// 1. Bootstrap modals (`.modal`). Skips modals containing the DTYP download popup, which is
			// tracked separately below via its own click-driven heuristic (rule 2) to avoid firing
			// modal_impression twice for the same popup.
			//
			// IMPORTANT: bound via jQuery's delegated .on(), NOT a plain addEventListener -- confirmed
			// live (secure-identity.js video modal, 2026-08-21) that the vendored Bootstrap 3.4.1
			// modal.js (see avg/j/global/09_modal_new.js) fires 'shown.bs.modal' purely through jQuery's
			// internal event system (jQuery's own tree-walk + handler queues), NOT via a real native
			// dispatchEvent() -- so a native addEventListener for it NEVER fires, no matter the timing.
			// (This turned out to also affect the existing AA tracking in singleDL-secondary.js, which
			// has the same native-addEventListener pattern -- earlier assumed proven-working, but never
			// actually verified live.)
			//
			// Delegating on `document` (rather than scanning + binding per-element) also means this
			// covers modals that don't exist in the DOM yet at bind time (e.g. secure-identity's
			// subscription-details-modal, injected fresh on click) with no MutationObserver/timing race
			// -- confirmed live both for a modal already present at page load (video) and one injected
			// and shown later (subscription details). Bound only once (see modalShownListenerBound).
			// Falls back to the old per-element scan + native addEventListener only if jQuery isn't on
			// the page at all (so there's no jQuery-only dispatch to miss in the first place).
			scanForModals: function () {
				if (window.jQuery) {
					if (!genDLObj.modalShownListenerBound) {
						genDLObj.modalShownListenerBound = true;
						window.jQuery(document).on('shown.bs.modal', '.modal', function (e) {
							var shownModal = e.target;
							if (shownModal.querySelector('[data-behavior="downloadPopup"]')) {
								return;
							}
							var modalType = shownModal.classList.contains('modal-full') ? 'fullscreen' : 'overlay';
							genDLObj.modalImpression(genDLObj.getModalName(shownModal), undefined, modalType);
						});
					}
					return;
				}

				var modals = document.querySelectorAll('.modal');
				for (var i = 0; i < modals.length; i++) {
					var modal = modals[i];
					if (modal.getAttribute('data-gendl-modal-claimed')) {
						continue;
					}
					modal.setAttribute('data-gendl-modal-claimed', '1');

					if (modal.querySelector('[data-behavior="downloadPopup"]')) {
						continue;
					}

					modal.addEventListener('shown.bs.modal', function (e) {
						var shownModal = e.target;
						var modalType = shownModal.classList.contains('modal-full') ? 'fullscreen' : 'overlay';
						genDLObj.modalImpression(genDLObj.getModalName(shownModal), undefined, modalType);
					});
				}
			},

			// 3./4. Watches `element` for the moment `visibleClass` is newly added to its classList (i.e.
			// the popup becomes visible), calling onShow(element) once per show -- fires again on every
			// subsequent re-show, unlike the one-shot "claimed" attribute used for scanning.
			watchClassToggle: function (element, visibleClass, onShow) {
				var wasVisible = element.classList.contains(visibleClass);
				new MutationObserver(function () {
					var nowVisible = element.classList.contains(visibleClass);
					if (nowVisible && !wasVisible) {
						onShow(element);
					}
					wasVisible = nowVisible;
				}).observe(element, { attributes: true, attributeFilter: ['class'] });
			},

			// 3. "Wrong download" (oops) notification overlay -- becomes visible via a `show-popup` class
			scanForNotificationOverlays: function () {
				var overlays = document.querySelectorAll('.js-notification-overlay-for-wrong-download');
				for (var i = 0; i < overlays.length; i++) {
					var overlay = overlays[i];
					if (overlay.getAttribute('data-gendl-modal-claimed')) {
						continue;
					}
					overlay.setAttribute('data-gendl-modal-claimed', '1');

					genDLObj.watchClassToggle(overlay, 'show-popup', function (shownOverlay) {
						genDLObj.modalImpression(genDLObj.getModalName(shownOverlay) || 'wrong-download-notification', 'oops', 'overlay');
					});
				}
			},

			// 4. Generic `.overlay-box` popups -- become visible via an `is-visible` class; identified by
			// their own `data-box` attribute (set by the trigger that opens them, see overlayPopup.js)
			scanForOverlayBoxes: function () {
				var overlays = document.querySelectorAll('.overlay-box');
				for (var i = 0; i < overlays.length; i++) {
					var overlay = overlays[i];
					if (overlay.getAttribute('data-gendl-modal-claimed')) {
						continue;
					}
					overlay.setAttribute('data-gendl-modal-claimed', '1');

					genDLObj.watchClassToggle(overlay, 'is-visible', function (shownOverlay) {
						var modalName = genDLObj.checkAttribute(shownOverlay, 'data-box') || genDLObj.getModalName(shownOverlay);
						genDLObj.modalImpression(modalName, undefined, 'overlay');
					});
				}
			},

			//
			// click
			// https://confluence.corp.nortonlifelock.com/spaces/AmplitudeMigration/pages/945664909/click
			// Fires on in-page-only actions (never a full page navigation -- that's page_viewed instead).
			//
			// AVG has no site-wide "custom link" marker for this (unlike Avast's s.tl() calls), and no
			// single component covers it either, so this is scoped to two concrete, verified patterns:
			//   1. In-page anchor navigation -- `a[href="#section"]` links (smooth-scroll or native jump
			//      to a same-page section, e.g. ppc-secure-browser.js / help-me-choose-tool-v2.js).
			//      Explicitly excludes modal/overlay triggers (data-toggle="modal", .overlay-box-trigger)
			//      -- those already have their own dedicated event (modal_impression), not this one.
			//   2. Accordion/expander toggles -- verified live (2026-08-21): `.js-question` is the FAQ
			//      accordion class actually used across dozens of AVG product pages (e.g.
			//      free-antivirus-download.js, secure-identity.js, internet-security.js, ...) -- NOT
			//      `.js-info-box .accordion-title` from frequently-asked.js, which appeared on none of
			//      the live pages checked and looks legacy/unused. Also covers Bootstrap
			//      `[data-toggle="collapse"]` "read more/less" expanders (11_collapse.js, confirmed live
			//      on free-antivirus-download), the comparison-table "see more/less details"
			//      (16_expand.js) and the facelift accordion (smb/components/facelift/accordion.js) --
			//      these last two are kept for coverage but unverified live.
			//
			// element_detail is intentionally never set -- the spec scopes it to My (NGP) properties only.
			//
			// precomputedText: optional pre-click text captured in the capture phase (see the click
			// listener registered above) -- used instead of re-reading it now, since for toggle
			// elements the visible label may have already changed by this point (bubble phase)
			click: function (element, clickName, precomputedText) {
				var properties = { click_name: clickName };
				var clickText = typeof precomputedText !== 'undefined' ? precomputedText : genDLObj.getClickText(element);
				if (clickText) {
					properties.click_text = clickText;
				}
				genDLObj.pushEvent('click', properties);
			},

			// Visible, whitespace-collapsed text of the clicked element, or undefined if empty (e.g. an
			// icon-only button). Uses innerText (not textContent) so hidden alternate-state text is
			// excluded -- confirmed live: the collapse "read more/less" toggle keeps both its "more" and
			// "less" labels in the DOM as sibling spans, only one visible (display:none) at a time.
			getClickText: function (element) {
				var text = (element.innerText || element.textContent || '').replace(/\s+/g, ' ').trim();
				return text || undefined;
			},

			// click_name has no established AVG naming convention, so it's built as
			// "<component>_<action>" -- component is the closest ancestor's data-cmp-name (AEM component
			// name, already used elsewhere in this file) when present. data-cmp-name turned out to be
			// rare/absent on the live pages checked (e.g. never present near the FAQ/collapse triggers),
			// so falls back to fallbackName, then the closest ancestor id (verified live: gives a
			// meaningful "faq" for FAQ items, though a less useful "body-inner" for the collapse toggle).
			buildClickName: function (element, actionLabel, fallbackName) {
				var componentName = genDLObj.checkAttribute(element.closest('[data-cmp-name]'), 'data-cmp-name')
					|| fallbackName
					|| (element.closest('[id]') || {}).id
					|| 'unknown-component';
				return componentName + '_' + actionLabel;
			}

			//
			// Individual events go here, e.g.:
			//
			// pageView: function () {
			// 	genDLObj.pushEvent('page_view', {
			// 		url: window.location.href,
			// 		title: document.title
			// 	});
			// },
			//
		};

		// page_viewed is fired from an inline <head> script in the CMS template, not from here --
		// see the MOVED comment above in the events object for why

		// Custom event dispatched by CHEQ (Ensighten) whenever the user changes their cookie consent
		window.addEventListener('cheqCookieConsentHasChanged', genDLObj.consentUpdated);

		// product_downloaded (only ever from the download-thank-you page -- see the rationale in the
		// comment above productDownloadedOnThankYouPage). dtp.js drives it, because it owns the moment the installer
		// navigation actually starts (~1s after DOM ready) which is the real "download initiated"
		// signal. Exposed as a global hook for it to call, plus a marker check for the reverse order
		// (this file loading only after dtp.js already fired), so whichever of the two runs second
		// still gets the event out. Idempotent either way, see thankYouDownloadFired.
		window.genDLThankYouDownload = genDLObj.productDownloadedOnThankYouPage;
		if (window.genDLThankYouDownloadPending) {
			genDLObj.productDownloadedOnThankYouPage();
		}

		// Capture-phase pre-read of click_text for expander/anchor targets, BEFORE any of the site's
		// own bubble-phase click handlers run on that same element -- confirmed live: e.g. the
		// collapse "read more/less" toggle flips its own visible label (via a handler bound directly
		// on the button) before our bubble-phase listener below would otherwise see it, so click_text
		// would wrongly capture the post-click state ("Hide more info") instead of what the user
		// actually saw and clicked ("More about ..."). Stashed on the event object itself so the two
		// listeners (capture here, bubble below) can share it for the same click.
		document.addEventListener('click', function (e) {
			var target = e.target.closest('.js-question, [data-toggle="collapse"], .js-info-box .accordion-title, .features-list-btn, .accordion-item__header, a[href^="#"]');
			if (target) {
				e.genDLClickText = genDLObj.getClickText(target);
			}
		}, true);

		// Delegated click listener for button/link driven events (mirrors singleDL-secondary.js pattern)
		document.addEventListener('click', function (e) {
			var link;

			// Installer download links are handled entirely on the thank-you page (see the comment above
			// productDownloadedOnThankYouPage) -- returning here keeps the same click from also being
			// counted as a file_downloaded
			if (link = e.target.closest('a[data-role="download-link"]')) {
				// modal_impression for the DTYP download popup (rule 2 -- see comment above
				// modalImpression) -- it doesn't reliably fire its own show event, so this mirrors the
				// same click-driven heuristic already used for AA tracking in singleDL-secondary.js
				var downloadPopup = document.querySelector('[data-behavior="downloadPopup"]');
				if (downloadPopup) {
					genDLObj.modalImpression(genDLObj.getModalName(downloadPopup) || 'download-popup', 'download', 'overlay');
				}
				return;
			}

			// file_downloaded (any other file link, e.g. PDFs, ZIPs -- installer extensions are skipped
			// inside fileDownloaded() itself as a second safety net)
			if (link = e.target.closest('a[href]')) {
				genDLObj.fileDownloaded(link);
			}

			// click: accordion/expander toggles (rule 2 -- see comment above the click function). Checked
			// before rule 1 below, since [data-toggle="collapse"] triggers are ALSO `a[href="#..."]`
			// anchors -- confirmed live (free-antivirus-download's "#collapse-33") -- and should only
			// count once, as an expand/collapse action rather than in-page navigation.
			var toggle = e.target.closest('.js-question, [data-toggle="collapse"], .js-info-box .accordion-title, .features-list-btn, .accordion-item__header');
			if (toggle) {
				genDLObj.click(toggle, genDLObj.buildClickName(toggle, 'expand-toggle'), e.genDLClickText);
			} else if (link = e.target.closest('a[href^="#"]')) {
				// click: in-page anchor navigation (rule 1 -- see comment above the click function). Bare
				// "#" hrefs and hrefs whose target doesn't actually exist on the page (e.g. placeholder
				// hrefs later swapped by JS, confirmed live on internet-security's "Buy Now" button) are
				// skipped, as are modal/overlay triggers -- those fire modal_impression instead, not click.
				var hash = link.getAttribute('href');
				var sectionId = hash.substring(1);
				var hasRealTarget = sectionId && (document.getElementById(sectionId) || document.getElementsByName(sectionId).length);
				if (hasRealTarget && !link.closest('[data-toggle="modal"]') && !link.closest('.overlay-box-trigger')) {
					genDLObj.click(link, genDLObj.buildClickName(link, 'page-scroll', sectionId), e.genDLClickText);
				}
			}
		});

		// video_started/video_progress/video_completed -- scan for YouTube iframes already in the DOM,
		// then keep watching for new ones added later (e.g. lazy-loaded video components). src changes
		// on iframes already being tracked are handled by their own `load` listener (see
		// wrapVideoIframe above) -- NOT observed here too, to avoid both firing a re-wrap at once.
		//
		// modal_impression -- scan for Bootstrap modals and custom overlay popups (rules 1, 3, 4 -- see
		// comment above modalImpression) already in the DOM, then keep watching for new ones added later
		// (e.g. lazy-loaded legal/subscription modals).
		genDLObj.scanForVideos();
		genDLObj.scanForModals();
		genDLObj.scanForNotificationOverlays();
		genDLObj.scanForOverlayBoxes();
		new MutationObserver(function () {
			genDLObj.scanForVideos();
			genDLObj.scanForModals();
			genDLObj.scanForNotificationOverlays();
			genDLObj.scanForOverlayBoxes();
		}).observe(document.body, { childList: true, subtree: true });

		//
		// Call individual event initializers here, e.g.:
		//
		// genDLObj.pageView();
		//

	} catch (error) {
		console.error(error);
		// Push error information to genDL
		genDL.push({
			event: 'system.error',
			type: error.name,
			message: error.message,
			description: 'genDL.js'
		});
	}
})();
